/**
 * @file Column component for displaying a marketplace service column
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { ChangeDetectorRef, OnDestroy } from '@angular/core';
import { EntityQuery, Entity, FetchStrategy, QueryOptions, QueryResult } from 'breeze-client';
import { DomainService } from '../../domain.service';
import { ColumnBase } from '../../base/columns/column-base/column-base';
import { ColumnProvider } from '../../base/column-provider';
import { OverlayService } from '../../core/overlay.service';
import { ComponentRegistry } from '../../core/component-registry';
import { ResourceService } from '../../resource.service';
import { BackendSettingsService } from '../../backend-settings.service';
/**
 * Column component for displaying a marketplace service value
 */
var ColumnMarketplaceServiceComponent = /** @class */ (function (_super) {
    tslib_1.__extends(ColumnMarketplaceServiceComponent, _super);
    /**
     * Constructs a ColumnMarketplaceServiceComponent
     * @param provider Provides the configuration information for the column
     * @param changeDetector The parameter is not used
     * @param domainService The domain service
     * @param dialogService The dialog service
     * @param translateService The translation service
     * @param alertService The alert service
     * @param {LocalisationService} localisationService The localisation service.
     */
    function ColumnMarketplaceServiceComponent(resourceService, provider, changeDetector, domainService, overlayService, backendSettingsService) {
        var _this = _super.call(this, provider, changeDetector) || this;
        _this.resourceService = resourceService;
        _this.domainService = domainService;
        _this.overlayService = overlayService;
        _this.backendSettingsService = backendSettingsService;
        _this.configuration = null;
        /** The priority of the demand on the marketplace */
        _this.priority = 'unknown';
        /** The total number of deployments with a resource attached on this demand */
        _this.filledSlots = 0;
        /** A value indicating whether the user's resource is already on one of the service's attendants */
        _this.taken = false;
        /** A value indicating whether the item is past the publishedTill date */
        _this.published = true;
        /** The attendants are stored in order to (un)subscribe to property changes */
        _this.attendants = [];
        /**
         * The configuration of the marketplace action for this type of column.
         * @private
         * @type {ActionConfiguration}
         * @memberOf ColumnMarketplaceServiceComponent
         */
        _this.action = null;
        if (_this.provider.entity &&
            _this.provider.entity['MarketplacePriority'] >= 0 &&
            ColumnMarketplaceServiceComponent.priorityName.length > _this.provider.entity['MarketplacePriority']) {
            _this.priority = ColumnMarketplaceServiceComponent.priorityName[_this.provider.entity['MarketplacePriority']];
        }
        _this.attendantQuery = EntityQuery.from('AttendantsOfService').expand('Resource');
        _this.attendantQuery.parameters = { oid: _this.provider.entity['Oid'] };
        _this.attendantQuery.queryOptions = new QueryOptions({ fetchStrategy: FetchStrategy.FromServer });
        _this.refresh();
        _this.subscriptionPool.add(_this.domainService.queryFinishedEvent.subscribe(function (result) {
            if (result.query.resourceName === 'MarketServices') {
                _this.refresh();
            }
        }));
        _this.action = _this.provider.field.format;
        return _this;
    }
    /** Register this column class for the column provider */
    ColumnMarketplaceServiceComponent.register = function () {
        ComponentRegistry.register('column', 'marketplace-service', ColumnMarketplaceServiceComponent);
    };
    /**
     * Unsubscribes the refresh subscription and attendant property changes
     */
    ColumnMarketplaceServiceComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
        this.attendants.forEach(function (attendant) {
            attendant.entityAspect.propertyChanged.unsubscribe(attendant['$propertyChangedSubscription']);
        });
    };
    /**
     * Returns a string describing the marketplace status of the service (see the template)
     */
    ColumnMarketplaceServiceComponent.prototype.getStatus = function () {
        if (!this.published) {
            return 'unpublished';
        }
        else if (this.taken) {
            return 'taken';
        }
        else if (this.totalSlots === 0) {
            return 'none';
        }
        else if (this.filledSlots === this.totalSlots) {
            return 'full';
        }
        else if (this.totalSlots != null && this.totalSlots > this.filledSlots) {
            return 'open';
        }
        return 'loading';
    };
    /**
     * Opens a dialog asking the user to confirm if he wants to accept the task
     * @param event The click event
     */
    ColumnMarketplaceServiceComponent.prototype.getOverlay = function (event) {
        event.stopPropagation();
        if (!this.action) {
            return;
        }
        this.overlayService.createOverlay({
            action: this.action,
            entity: this.provider.entity
        });
    };
    /**
     * Does nothing
     */
    ColumnMarketplaceServiceComponent.prototype.startOptimisedChangeDetection = function () {
        // Overriding this method causes the component to refresh by Angular's default rather than by an entity change.
        // The column displays a related entity count and not any of the entity's properties.
    };
    /**
     * Retrieves the deployments
     */
    ColumnMarketplaceServiceComponent.prototype.refresh = function () {
        var _this = this;
        this.totalSlots = null;
        if (!this.provider.entity) {
            return;
        }
        // All deployments are re-queried
        this.attendants.forEach(function (attendant) {
            attendant.entityAspect.propertyChanged.unsubscribe(attendant['$propertyChangedSubscription']);
        });
        this.attendants = [];
        // Fix for #1706
        this.taken = false;
        // Optimistic approach
        this.published = true;
        // Keep track of the published state
        var isPublished = true;
        // First we check if entity is published
        if (this.provider.entity['Start']) {
            isPublished = this.backendSettingsService.publishedTill.isAfter(this.provider.entity['Start']);
        }
        // Only proceed if entity is published (to prevent unnecessary requests)
        if (isPublished) {
            // Make column in published state
            this.published = true;
            // Execute the query and set total slots, filled slots and taken etc.
            this.domainService.entityManager.executeQuery(this.attendantQuery).then(function (response) {
                _this.totalSlots = response.results.length;
                _this.filledSlots = 0;
                _this.taken = false;
                _this.attendant = null;
                // Detach attendants that do not exist any more.
                var collection = _this.provider.entity['Attendants'];
                if (collection) {
                    var i = collection.length;
                    var _loop_1 = function () {
                        // Remove deleted attendants from cache.
                        i--;
                        var item = collection[i];
                        if (response.results.find(function (entity) { return item['Oid'] === entity['Oid']; }) == null) {
                            collection.splice(i, 1);
                            if (item['$propertyChangedSubscription']) {
                                item.entityAspect.propertyChanged.unsubscribe(item['$propertyChangedSubscription']);
                            }
                            item.entityAspect.setDetached();
                        }
                    };
                    while (i > 0) {
                        _loop_1();
                    }
                }
                response.results.forEach(function (attendant) {
                    _this.attendants.push(attendant);
                    // This subscription is required in case the user chooses to attend through the accept-service action
                    attendant['$propertyChangedSubscription'] = attendant.entityAspect.propertyChanged.subscribe(function (data) {
                        if (data.propertyName === 'Resource_Oid' && data.newValue === _this.resourceService.getResourceOid()) {
                            _this.taken = true;
                        }
                    });
                    if (attendant['Resource']) {
                        _this.filledSlots++;
                        if (attendant['Resource_Oid'] === _this.resourceService.getResourceOid()) {
                            _this.taken = true;
                        }
                    }
                    else if (!_this.attendant) {
                        _this.attendant = attendant;
                    }
                });
            });
        }
        else {
            // Give the column 'a loading state' to indicate that it is working
            setTimeout(function () {
                // Make column in unpublished state
                _this.published = false;
            }, 500);
        }
    };
    /** The string representation of marketplace priorities */
    ColumnMarketplaceServiceComponent.priorityName = ['low', 'normal', 'high'];
    return ColumnMarketplaceServiceComponent;
}(ColumnBase));
export { ColumnMarketplaceServiceComponent };
